/**
 * Put course calendars in the person's own Google Calendar list, from their
 * browser, with a one-time token IMAM never sees (ADR 0040).
 *
 * IMAM's server owns access: it shares each course calendar read-only with the
 * person's school account. Google still needs that account to add the shared
 * calendar to its own list, which only the account itself may do. So the
 * browser asks Google for a token limited to
 * `https://www.googleapis.com/auth/calendar.calendarlist`, with
 * `include_granted_scopes: false`, the school account as the hint and the
 * school's domain, inserts each ready calendar, and drops the token. The token
 * goes only to Google: the share call carries nothing of it.
 *
 * The token is asked for first, inside the press: the server's share can take
 * seconds, and a popup opened after it would be blocked.
 */

import {
  googleDriveBrowserRuntime,
  requestGoogleDriveToken,
  type GoogleDriveFailureReason,
  type GoogleDriveTokenResult,
} from "@/lib/google-drive-picker";

export const GOOGLE_CALENDAR_LIST_SCOPE = "https://www.googleapis.com/auth/calendar.calendarlist";
const CALENDAR_LIST_URL = "https://www.googleapis.com/calendar/v3/users/me/calendarList";
/** One insert's ceiling; Google answers these in well under a second. */
export const GOOGLE_CALENDAR_INSERT_TIMEOUT_MS = 15_000;

/** What the server says the browser needs: never a secret, never an email. */
export interface CalendarSubscribeClient {
  client_id: string;
  hosted_domain: string;
  /** The school account's Google id. */
  login_hint: string;
}

export interface SharedCalendars {
  ready: { course_id: string; calendar_id: string }[];
  pending: string[];
}

/**
 * - `added`: each ready calendar is in the person's list (one already there
 *   counts). `pending` courses are still being prepared.
 * - `wrong_account`: Google refused the insert, so the token belongs to an
 *   account the calendars are not shared with: not the school account.
 * - `failed`: consent refused, popup blocked, or Google did not answer.
 * - `cancelled`: the person closed Google's window. Said nothing about.
 */
export type CalendarSubscribeOutcome =
  | { kind: "added"; added: number; pending: number; failed: number }
  | { kind: "wrong_account" }
  | { kind: "failed"; reason: GoogleDriveFailureReason }
  | { kind: "cancelled" };

export type CalendarInsertResult = "added" | "refused" | "failed";

export interface CalendarSubscribeRuntime {
  loadIdentity: () => Promise<void>;
  requestToken: (client: CalendarSubscribeClient) => Promise<GoogleDriveTokenResult>;
  insert: (accessToken: string, calendarId: string) => Promise<CalendarInsertResult>;
}

/**
 * Add one calendar to the list. Exported so the request itself is proved: the
 * address, the token only in the Authorization header, and the body.
 */
export async function insertCalendarListEntry(
  fetcher: typeof fetch,
  accessToken: string,
  calendarId: string,
): Promise<CalendarInsertResult> {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), GOOGLE_CALENDAR_INSERT_TIMEOUT_MS);
  try {
    const response = await fetcher(CALENDAR_LIST_URL, {
      method: "POST",
      headers: { Authorization: `Bearer ${accessToken}`, "Content-Type": "application/json" },
      body: JSON.stringify({ id: calendarId, selected: true }),
      credentials: "omit",
      referrerPolicy: "no-referrer",
      signal: controller.signal,
    });
    // 409: already in the list, which is what the press asked for.
    if (response.ok || response.status === 409) return "added";
    // Not shared with this account: the token is not the school account's.
    if (response.status === 403 || response.status === 404) return "refused";
    return "failed";
  } catch {
    return "failed";
  } finally {
    clearTimeout(timer);
  }
}

export const googleCalendarBrowserRuntime: CalendarSubscribeRuntime = {
  loadIdentity: () => googleDriveBrowserRuntime.loadIdentity(),
  requestToken(client) {
    const oauth = window.google?.accounts?.oauth2;
    if (!oauth) return Promise.resolve({ kind: "failed", reason: "unresponsive" });
    return requestGoogleDriveToken(
      oauth, client.client_id, GOOGLE_CALENDAR_LIST_SCOPE, client.login_hint, undefined, client.hosted_domain,
    );
  },
  insert: (accessToken, calendarId) => insertCalendarListEntry(window.fetch.bind(window), accessToken, calendarId),
};

/** Load Google's sign-in script ahead of the press, so the press opens its window at once. */
export function preloadGoogleCalendarSubscribe(runtime: CalendarSubscribeRuntime = googleCalendarBrowserRuntime) {
  void runtime.loadIdentity().catch(() => undefined);
}

/**
 * The press: token first, then the server's share, then each insert. `share`
 * is IMAM's call and takes nothing, so the token cannot travel with it.
 */
export async function subscribeCourseCalendars(
  client: CalendarSubscribeClient,
  share: () => Promise<SharedCalendars>,
  runtime: CalendarSubscribeRuntime = googleCalendarBrowserRuntime,
): Promise<CalendarSubscribeOutcome> {
  let token: GoogleDriveTokenResult = { kind: "cancelled" };
  try {
    try {
      await runtime.loadIdentity();
    } catch {
      return { kind: "failed", reason: "unresponsive" };
    }
    token = await runtime.requestToken(client);
    if (token.kind === "cancelled") return { kind: "cancelled" };
    if (token.kind === "failed") return { kind: "failed", reason: token.reason };
    const accessToken = token.accessToken;
    // IMAM's failure is the caller's to say: it carries the server's words.
    const shared = await share();
    let added = 0;
    let refused = 0;
    let failed = 0;
    for (const { calendar_id } of shared.ready) {
      const result = await runtime.insert(accessToken, calendar_id);
      if (result === "added") added += 1;
      else if (result === "refused") refused += 1;
      else failed += 1;
    }
    if (refused > 0 && added === 0) return { kind: "wrong_account" };
    return { kind: "added", added, pending: shared.pending.length, failed: failed + refused };
  } finally {
    // The only reference to the token lives in this call.
    token = { kind: "cancelled" };
  }
}
